import { useLazyQuery } from '@apollo/client';
import { QUERY_fetchOrderShiftBlockOrders } from 'queries';
import {
  FC,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createApolloCache, createApolloClient } from 'providers/ApolloProvider';

const PAGE_SIZE = 10;

const shiftBlockApolloClient = createApolloClient({ cache: createApolloCache() });

export interface ShiftBlockOrder {
  displayId: number;
  relayId: string;
  startAt: string;
  timeZone: string;
  orderSchedule: {
    id: number;
    startTime: string;
    endTime: string;
  };
  position: {
    name: string;
  };
  community: {
    name: string;
  };
  subFacilityAddress?: {
    name: string;
  } | null;
}

export interface ShiftBlockOrdersDataContextType {
  orders: ShiftBlockOrder[];
  isLoading: boolean;
  hasNextPage: boolean;
  loadMore: () => void;
  leadShiftId: number | null;
}

interface FetchOrderShiftBlockOrdersResult {
  node: {
    shiftBlocks?: {
      nodes?: Array<{
        id: number;
        displayId: number;
        leadShift?: { displayId: number } | null;
        orders?: {
          nodes?: ShiftBlockOrder[];
          pageInfo?: { hasNextPage: boolean; endCursor: string | null };
        };
      }>;
    };
  } | null;
}

export const ShiftBlockOrdersDataContext = createContext<ShiftBlockOrdersDataContextType>({
  orders: [],
  isLoading: true,
  hasNextPage: false,
  loadMore: () => {},
  leadShiftId: null,
});

interface ShiftBlockOrdersDataProviderProps {
  orderRelayId: string;
  excludeOrderId?: number;
}

export const ShiftBlockOrdersDataProvider: FC<ShiftBlockOrdersDataProviderProps> = ({
  children,
  orderRelayId,
  excludeOrderId,
}) => {
  const [orders, setOrders] = useState<ShiftBlockOrder[]>([]);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [leadShiftId, setLeadShiftId] = useState<number | null>(null);
  const endCursorRef = useRef<string | null>(null);

  const [fetchShiftBlockOrders, { loading }] = useLazyQuery<FetchOrderShiftBlockOrdersResult>(
    QUERY_fetchOrderShiftBlockOrders,
    {
      client: shiftBlockApolloClient,
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        const block = data?.node?.shiftBlocks?.nodes?.[0];
        if (!block) return;
        if (block.leadShift?.displayId) {
          setLeadShiftId(block.leadShift.displayId);
        }
        if (block.orders) {
          let newOrders = block.orders.nodes ?? [];
          if (excludeOrderId) {
            newOrders = newOrders.filter((order) => order.displayId !== excludeOrderId);
          }
          setOrders((prev) => (endCursorRef.current ? [...prev, ...newOrders] : newOrders));
          setHasNextPage(block.orders.pageInfo?.hasNextPage ?? false);
          endCursorRef.current = block.orders.pageInfo?.endCursor ?? null;
        }
      },
    },
  );

  useEffect(() => {
    if (orderRelayId) {
      endCursorRef.current = null;
      fetchShiftBlockOrders({
        variables: { orderRelayId, first: PAGE_SIZE },
      });
    }
  }, [orderRelayId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMore = useCallback(() => {
    if (hasNextPage && endCursorRef.current && !loading) {
      fetchShiftBlockOrders({
        variables: {
          orderRelayId,
          first: PAGE_SIZE,
          after: endCursorRef.current,
        },
      });
    }
  }, [hasNextPage, loading, orderRelayId, fetchShiftBlockOrders]);

  const value = useMemo(
    () => ({
      orders,
      isLoading: loading && orders.length === 0,
      hasNextPage,
      loadMore,
      leadShiftId,
    }),
    [orders, loading, hasNextPage, loadMore, leadShiftId],
  );

  return (
    <ShiftBlockOrdersDataContext.Provider value={value}>
      {children}
    </ShiftBlockOrdersDataContext.Provider>
  );
};

export const useShiftBlockOrdersDataContext = () => useContext(ShiftBlockOrdersDataContext);
